/**
 * Headless-friendly forge host for @repo/office-render.
 * Exposes window.__LAYOUT_RENDER__ for Playwright / CF Browser Rendering.
 */
import { useCallback, useEffect, useState } from "react"
import {
  IsoCanvas,
  captureLayoutSnapshots,
  createEmptyDocument,
  getSceneHandle,
  useDesign,
  useEditor,
  type LayoutSnapshotBlobs,
  type OfficeDocument,
} from "@repo/office-render"

export type LayoutRenderBridge = {
  ready: boolean
  webgl: () => WebGLProbe
  setDocument: (doc: OfficeDocument) => void
  capture: () => Promise<Record<keyof LayoutSnapshotBlobs, string>>
  status: string
}

export type WebGLProbe = {
  ok: boolean
  vendor?: string
  renderer?: string
  version?: string
  error?: string
}

declare global {
  interface Window {
    __LAYOUT_RENDER__?: LayoutRenderBridge
  }
}

const STAGE = 512

function settle(): Promise<void> {
  return new Promise((r) =>
    requestAnimationFrame(() => requestAnimationFrame(() => r())),
  )
}

async function waitHandle(ms = 15_000): Promise<void> {
  const end = Date.now() + ms
  while (Date.now() < end) {
    if (getSceneHandle()) {
      await settle()
      await settle()
      return
    }
    await settle()
  }
  throw new Error("scene handle timeout")
}

async function blobsToBase64(
  blobs: LayoutSnapshotBlobs,
): Promise<Record<keyof LayoutSnapshotBlobs, string>> {
  const out = {} as Record<keyof LayoutSnapshotBlobs, string>
  for (const key of Object.keys(blobs) as (keyof LayoutSnapshotBlobs)[]) {
    const buf = await blobs[key].arrayBuffer()
    const bytes = new Uint8Array(buf)
    let binary = ""
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]!)
    }
    out[key] = btoa(binary)
  }
  return out
}

function probeWebGL(): WebGLProbe {
  try {
    const canvas = document.createElement("canvas")
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")
    if (!gl) return { ok: false, error: "no webgl context" }
    const webgl = gl as WebGLRenderingContext
    const dbg = webgl.getExtension("WEBGL_debug_renderer_info")
    return {
      ok: true,
      vendor: dbg
        ? String(webgl.getParameter(dbg.UNMASKED_VENDOR_WEBGL))
        : String(webgl.getParameter(webgl.VENDOR)),
      renderer: dbg
        ? String(webgl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
        : String(webgl.getParameter(webgl.RENDERER)),
      version: String(webgl.getParameter(webgl.VERSION)),
    }
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export function ForgeApp() {
  const loadDocument = useDesign((s) => s.loadDocument)
  const requestFit = useEditor((s) => s.requestFit)
  const [status, setStatus] = useState("booting")
  const [ready, setReady] = useState(false)

  const setDocument = useCallback(
    (doc: OfficeDocument) => {
      loadDocument(doc)
      useDesign.temporal.getState().clear()
      requestFit()
      setStatus("document-loaded")
    },
    [loadDocument, requestFit],
  )

  const runCapture = useCallback(async () => {
    setStatus("capturing")
    await waitHandle()
    requestFit()
    await settle()
    await settle()
    const blobs = await captureLayoutSnapshots()
    setStatus("captured")
    return blobsToBase64(blobs)
  }, [requestFit])

  useEffect(() => {
    setDocument(createEmptyDocument())
    void waitHandle()
      .then(() => {
        setReady(true)
        setStatus("ready")
      })
      .catch((e) => {
        setStatus(`error: ${e instanceof Error ? e.message : String(e)}`)
      })
  }, [setDocument])

  useEffect(() => {
    window.__LAYOUT_RENDER__ = {
      get ready() {
        return ready
      },
      webgl: probeWebGL,
      setDocument,
      capture: runCapture,
      get status() {
        return status
      },
    }
    return () => {
      delete window.__LAYOUT_RENDER__
    }
  }, [ready, setDocument, runCapture, status])

  return (
    <div
      style={{ width: STAGE, height: STAGE, margin: 0, position: "relative" }}
      data-status={status}
      data-ready={ready ? "1" : "0"}
    >
      <div style={{ width: STAGE, height: STAGE }}>
        <IsoCanvas />
      </div>
      <div
        style={{
          position: "absolute",
          left: 8,
          bottom: 8,
          color: "#9aa",
          font: "12px ui-monospace, monospace",
          pointerEvents: "none",
        }}
      >
        layout-render · {status}
      </div>
    </div>
  )
}
